import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000; // Refresh token every 30 minutes of activity
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes inactivity timeout
const INACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check inactivity every 60 seconds (was 30)
const SESSION_ID_KEY = 'clinic_session_id';
const TOKEN_KEY = 'clinic_token';
const LAST_ACTIVITY_KEY = 'clinic_last_activity';

// Generate a unique session ID for this browser tab
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Global flag to prevent multiple redirects
let isRedirecting = false;

// Set up axios interceptor for handling 401 errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isRedirecting) {
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      const isRefreshRequest = error.config?.url?.includes('/auth/refresh');
      
      // Don't redirect for login attempts - just show error
      if (!isLoginRequest && !isRefreshRequest) {
        isRedirecting = true;
        
        // Clear token and redirect
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('clinic_working_location');
        sessionStorage.removeItem(SESSION_ID_KEY);
        delete axios.defaults.headers.common['Authorization'];
        
        toast.error('Your session has expired. Please log in again.');
        
        // Redirect to login
        setTimeout(() => {
          window.location.href = '/login';
          isRedirecting = false;
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  
  // Initialize lastActivity - always start fresh to avoid premature logout
  // The actual activity time will be set properly on login or when activity events fire
  const lastActivityRef = useRef(Date.now());
  const refreshTimeoutRef = useRef(null);
  const inactivityCheckRef = useRef(null);
  const hasInitializedActivityRef = useRef(false);
  const sessionIdRef = useRef(sessionStorage.getItem(SESSION_ID_KEY) || generateSessionId());
  
  // Store session ID in sessionStorage (tab-specific, not synced)
  useEffect(() => {
    sessionStorage.setItem(SESSION_ID_KEY, sessionIdRef.current);
  }, []);
  
  // Working location - the location the user is currently working at
  const [workingLocation, setWorkingLocationState] = useState(
    JSON.parse(localStorage.getItem('clinic_working_location')) || null
  );

  // Listen for storage events (when another tab logs in/out)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === TOKEN_KEY) {
        if (e.newValue && e.newValue !== token) {
          // Another tab logged in with a different token
          // Don't automatically log out - just update our token
          console.log('Token changed in another tab, updating...');
          setToken(e.newValue);
          axios.defaults.headers.common['Authorization'] = `Bearer ${e.newValue}`;
        } else if (!e.newValue && token) {
          // Another tab logged out
          console.log('Logged out from another tab');
          setToken(null);
          setUser(null);
          setClinic(null);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token]);

  // Track user activity - persist to localStorage to survive page reloads
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    hasInitializedActivityRef.current = true;
  }, []);

  const refreshToken = useCallback(async () => {
    if (!token) return;
    
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    
    // If user has been inactive for too long, logout
    if (timeSinceActivity > INACTIVITY_TIMEOUT) {
      console.log('Session expired due to inactivity');
      logout();
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`);
      const { access_token, user: userData, clinic: clinicData } = response.data;
      localStorage.setItem(TOKEN_KEY, access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      if (clinicData) {
        setClinic(clinicData);
      }
      setMustResetPassword(userData.must_reset_password || false);
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (error.response?.status === 401) {
        logout();
      }
    }
  }, [token]);

  // Set up activity listeners and token refresh
  useEffect(() => {
    if (token) {
      // Update activity immediately when token is present (user just logged in or page reloaded)
      updateActivity();
      
      // Set up activity listeners
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
      events.forEach(event => {
        window.addEventListener(event, updateActivity);
      });

      // Set up token refresh interval
      refreshTimeoutRef.current = setInterval(() => {
        refreshToken();
      }, TOKEN_REFRESH_INTERVAL);

      // Set up inactivity check interval (checks every 60 seconds)
      inactivityCheckRef.current = setInterval(() => {
        // Skip check if activity hasn't been initialized yet (user just logged in)
        if (!hasInitializedActivityRef.current) {
          return;
        }
        
        const timeSinceActivity = Date.now() - lastActivityRef.current;
        if (timeSinceActivity > INACTIVITY_TIMEOUT) {
          console.log('Session expired due to inactivity. Time since last activity:', Math.round(timeSinceActivity / 1000), 'seconds');
          // Clear intervals first
          if (refreshTimeoutRef.current) {
            clearInterval(refreshTimeoutRef.current);
          }
          if (inactivityCheckRef.current) {
            clearInterval(inactivityCheckRef.current);
          }
          // Clear token and redirect
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem('clinic_working_location');
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          sessionStorage.removeItem(SESSION_ID_KEY);
          delete axios.defaults.headers.common['Authorization'];
          toast.error('Session expired due to inactivity. Please log in again.');
          window.location.href = '/login';
        }
      }, INACTIVITY_CHECK_INTERVAL);

      return () => {
        events.forEach(event => {
          window.removeEventListener(event, updateActivity);
        });
        if (refreshTimeoutRef.current) {
          clearInterval(refreshTimeoutRef.current);
        }
        if (inactivityCheckRef.current) {
          clearInterval(inactivityCheckRef.current);
        }
      };
    }
  }, [token, updateActivity, refreshToken]);

  useEffect(() => {
    if (token && !user) {
      // Only fetch user on initial load (token exists but user not yet loaded)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else if (!token) {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setMustResetPassword(response.data.must_reset_password || false);
      
      // Fetch clinic data if user has clinic_id
      if (response.data.clinic_id) {
        try {
          const clinicRes = await axios.get(`${API_URL}/api/clinics/${response.data.clinic_id}`);
          setClinic(clinicRes.data);
        } catch (clinicErr) {
          console.error('Failed to fetch clinic:', clinicErr);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Refresh clinic data
  const refreshClinic = async () => {
    if (user?.clinic_id) {
      try {
        const response = await axios.get(`${API_URL}/api/clinics/${user.clinic_id}`);
        setClinic(response.data);
      } catch (error) {
        console.error('Failed to refresh clinic:', error);
      }
    }
  };

  // Generate device fingerprint for 2FA - matches backend algorithm
  const getDeviceFingerprint = async () => {
    const userAgent = window.navigator.userAgent;
    // Use SubtleCrypto SHA-256 to match backend's hashlib.sha256
    const encoder = new TextEncoder();
    const data = encoder.encode(userAgent);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    // Return first 32 characters like backend
    return hashHex.substring(0, 32);
  };

  const login = async (email, password) => {
    const deviceFingerprint = await getDeviceFingerprint();
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
      device_fingerprint: deviceFingerprint
    });
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return { requires_2fa: true, message: response.data.message };
    }
    
    // Normal login flow
    const { access_token, user: userData, clinic: clinicData } = response.data;
    
    // Store token synchronously and verify it was stored
    localStorage.setItem(TOKEN_KEY, access_token);
    
    // Double-check token was persisted (mobile browsers can be async)
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      console.error('Token failed to persist to localStorage');
      throw new Error('Failed to save login session');
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setClinic(clinicData || null);
    setMustResetPassword(userData.must_reset_password || false);
    
    // Set default working location from clinic data on login
    // This ensures dashboard shows stats for the default location, not all locations
    if (clinicData?.default_location_id && clinicData?.default_location_name) {
      const defaultLocation = {
        id: clinicData.default_location_id,
        name: clinicData.default_location_name
      };
      localStorage.setItem('clinic_working_location', JSON.stringify(defaultLocation));
      setWorkingLocationState(defaultLocation);
    } else if (userData.location_ids?.length === 1 && userData.location_names?.length === 1) {
      // If user has only one location, set it as working location
      const singleLocation = {
        id: userData.location_ids[0],
        name: userData.location_names[0]
      };
      localStorage.setItem('clinic_working_location', JSON.stringify(singleLocation));
      setWorkingLocationState(singleLocation);
    }
    
    // Reset activity timer on successful login
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    hasInitializedActivityRef.current = true;
    return { user: userData };
  };

  const verify2FA = async (email, code, trustDevice = false) => {
    const deviceFingerprint = await getDeviceFingerprint();
    const response = await axios.post(`${API_URL}/api/auth/verify-2fa`, {
      email,
      code,
      device_fingerprint: deviceFingerprint,
      trust_device: trustDevice
    });
    
    const { access_token, user: userData, clinic: clinicData } = response.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setClinic(clinicData || null);
    setMustResetPassword(userData.must_reset_password || false);
    
    // Set default working location from clinic data on 2FA verification
    if (clinicData?.default_location_id && clinicData?.default_location_name) {
      const defaultLocation = {
        id: clinicData.default_location_id,
        name: clinicData.default_location_name
      };
      localStorage.setItem('clinic_working_location', JSON.stringify(defaultLocation));
      setWorkingLocationState(defaultLocation);
    } else if (userData.location_ids?.length === 1 && userData.location_names?.length === 1) {
      // If user has only one location, set it as working location
      const singleLocation = {
        id: userData.location_ids[0],
        name: userData.location_names[0]
      };
      localStorage.setItem('clinic_working_location', JSON.stringify(singleLocation));
      setWorkingLocationState(singleLocation);
    }
    
    // Reset activity timer on successful 2FA verification
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    hasInitializedActivityRef.current = true;
    return userData;
  };

  const resetPassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    const { access_token } = response.data;
    if (access_token) {
      localStorage.setItem(TOKEN_KEY, access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setMustResetPassword(false);
    }
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    return response.data;
  };

  const logout = (showExpiredMessage = false) => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('clinic_working_location');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setClinic(null);
    setWorkingLocationState(null);
    setMustResetPassword(false);
    hasInitializedActivityRef.current = false;
    if (refreshTimeoutRef.current) {
      clearInterval(refreshTimeoutRef.current);
    }
    if (inactivityCheckRef.current) {
      clearInterval(inactivityCheckRef.current);
    }
  };

  // Set the working location (persisted to localStorage)
  const setWorkingLocation = (location) => {
    if (location) {
      localStorage.setItem('clinic_working_location', JSON.stringify(location));
    } else {
      localStorage.removeItem('clinic_working_location');
    }
    setWorkingLocationState(location);
  };

  const isSuperAdmin = () => user?.role === 'super_admin';
  const isClinicAdmin = () => user?.role === 'clinic_admin';
  const isDoctor = () => user?.role === 'doctor';
  const isFrontDesk = () => user?.role === 'front_desk';
  const isInventoryManager = () => user?.role === 'inventory_manager';
  
  // Get the user's clinic_id
  const getClinicId = () => user?.clinic_id;

  return (
    <AuthContext.Provider value={{
      user,
      clinic,
      token,
      loading,
      login,
      verify2FA,
      register,
      logout,
      resetPassword,
      mustResetPassword,
      isSuperAdmin,
      isClinicAdmin,
      isDoctor,
      isFrontDesk,
      isInventoryManager,
      getClinicId,
      workingLocation,
      setWorkingLocation,
      refreshClinic
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
